// Learned event sequences (migration 038): "launches from Khomein are
// followed by impacts at Muwaffaq Salti ~8 minutes later, seen 7 times."
//
// The chips this module renders are EXPECTATIONS and INFERENCES, never
// reports, and per the basis rule the words say so — "past launches here →",
// "may follow", "seen n× before". Styling alone is deniable; a screenshot of
// this feed ends up in someone else's report.
//
// Three tiers of claim, weakest wording for the weakest evidence:
//   launch row  -> what history says usually comes next (pattern required)
//   impact row  -> which launch it may answer to (pattern-backed)
//   impact row  -> a bare temporal fact ("launch at X 7m earlier") when no
//                  pattern exists but the timing is tight — stated as fact
//                  because it IS one, with no causal wording at all.

import type { EntityDto } from './api';
import { get } from './api';
import { normalizePlace } from './situations';
import { whenOf } from './when';

export interface SequencePattern {
  origin: string;
  originLabel: string;
  dest: string;
  destLabel: string;
  n: number;
  medianDelayS: number;
  lastAt: string;
  plausible: boolean | null;
  confidence: number | null;
  note: string | null;
}

let patterns: SequencePattern[] = [];

/** Test seam — the fetch path is loadSequences(). */
export function setPatterns(next: SequencePattern[]): void {
  patterns = next;
}

export async function loadSequences(): Promise<void> {
  try {
    patterns = (await get<{ patterns: SequencePattern[] }>('/api/sequences'))
      .patterns ?? [];
  } catch { /* no patterns, no chips — the feed stands */ }
}

// A model-rejected pattern is dead for display; unjudged (null) is usable —
// the counting stands on its own and the words never over-claim.
const usable = (p: SequencePattern): boolean => p.plausible !== false;

/** The strongest expectation from launches at this place, or null. */
export function expectationFor(place: unknown): SequencePattern | null {
  const key = normalizePlace(place);
  if (!key) return null;
  const hits = patterns.filter((p) => p.origin === key && usable(p));
  return hits.length ? hits.reduce((a, b) => (b.n > a.n ? b : a)) : null;
}

/** The no-pattern chip's tighter window: a bare temporal fact is only worth
 *  stating when the timing is tight. */
export const FACT_WINDOW_MS = 20 * 60_000;
/** Mirror of the backend's WINDOW_S — a pattern-backed precedent may reach
 *  as far as the miner itself paired. */
export const PATTERN_WINDOW_MS = 45 * 60_000;

const DEST_KINDS = new Set(['impact', 'explosion', 'interception']);

export interface Precedent {
  launch: EntityDto;
  pattern: SequencePattern | null;
  deltaMs: number;
}

interface LaunchRef { launch: EntityDto; at: number; originKey: string }

// The launches in `recent`, extracted ONCE per list. precedentFor is asked for
// every impact-family row the feed paints, and it used to walk the whole event
// list each time — hundreds of rows × thousands of events, with a timestamp
// parse and a place normalisation per step — which made it the single most
// expensive line of a feed repaint. The list the feed passes is replaced, never
// mutated, on each poll, so keying on its identity (guarded by its length) is
// exact.
const LAUNCH_MEMO = new WeakMap<EntityDto[], {
  n: number; first: EntityDto | undefined; last: EntityDto | undefined; refs: LaunchRef[];
}>();

function launchesOf(recent: EntityDto[]): LaunchRef[] {
  const hit = LAUNCH_MEMO.get(recent);
  if (hit && hit.n === recent.length && hit.first === recent[0]
      && hit.last === recent[recent.length - 1]) {
    return hit.refs;
  }
  const refs: LaunchRef[] = [];
  for (const l of recent) {
    if (String(l.attrs.kind ?? '') !== 'launch') continue;
    const originKey = normalizePlace(l.attrs.place);
    if (!originKey) continue;
    refs.push({ launch: l, at: whenOf(l).at, originKey });
  }
  LAUNCH_MEMO.set(recent, {
    n: recent.length, first: recent[0], last: recent[recent.length - 1], refs,
  });
  return refs;
}

/** For an impact-family event: the recent launch it may answer to, preferring
 *  pattern-backed precedents, then the nearest in time. */
export function precedentFor(e: EntityDto, recent: EntityDto[]): Precedent | null {
  if (!DEST_KINDS.has(String(e.attrs.kind ?? ''))) return null;
  const at = whenOf(e).at;
  const destKey = normalizePlace(e.attrs.place);
  let best: Precedent | null = null;
  for (const { launch: l, at: launchAt, originKey } of launchesOf(recent)) {
    const delta = at - launchAt;
    if (!(delta > 0 && delta <= PATTERN_WINDOW_MS)) continue;
    if (originKey === destKey) continue;
    const pattern = patterns.find((p) =>
      p.origin === originKey && p.dest === destKey && usable(p)) ?? null;
    if (!pattern && delta > FACT_WINDOW_MS) continue;
    if (!best
        || (pattern !== null && best.pattern === null)
        || ((pattern !== null) === (best.pattern !== null)
            && delta < best.deltaMs)) {
      best = { launch: l, pattern, deltaMs: delta };
    }
  }
  return best;
}

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** The feed chip for one event, or ''. `recent` is whatever the feed holds —
 *  the launches a precedent can be found among. */
export function sequenceChipHtml(e: EntityDto, recent: EntityDto[]): string {
  if (String(e.attrs.kind ?? '') === 'launch') {
    const p = expectationFor(e.attrs.place);
    if (!p) return '';
    const mins = Math.max(1, Math.round(p.medianDelayS / 60));
    const title = `${p.note ? `${p.note} — ` : ''}An expectation derived from `
      + `${p.n} prior sequences in the event history, not a report.`;
    return `<span class="s seq-line" title="${esc(title)}">⤳ past launches here `
      + `→ ${esc(p.destLabel)} ~${mins}m later (${p.n}×)</span>`;
  }
  const prec = precedentFor(e, recent);
  if (!prec) return '';
  const mins = Math.max(1, Math.round(prec.deltaMs / 60_000));
  const place = String(prec.launch.attrs.place ?? 'a launch site');
  if (prec.pattern) {
    const title = `${prec.pattern.note ? `${prec.pattern.note} — ` : ''}An `
      + `inference from timing plus ${prec.pattern.n} prior sequences; the `
      + `report itself claims no connection.`;
    return `<span class="s seq-line" title="${esc(title)}">⤳ may follow the `
      + `${esc(place)} launch ${mins}m earlier (seen ${prec.pattern.n}× before)</span>`;
  }
  return `<span class="s seq-line seq-fact" title="A temporal fact only — no `
    + `claimed connection.">⤳ launch at ${esc(place)} ${mins}m earlier</span>`;
}
